import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "@mui/material";

import chatPlaceholder from "../../../../assets/images/chatPlaceholder.svg";

import styles from "./likes.module.css";

import UpgradeModal from "../upgradenow/upgradenow";
import { useSocket } from "../../../../Context/SockerContext";
import { useAppContext } from "../../../../Context/UseContext";
import {
  getProfileImageUrl,
  resolveProfileId,
} from "../../../../utils/serverUtils";

import * as userActions from "../../../../actions/user";
import * as messageActon from "../../../../actions/Message";

export default function Likes() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { isPremium } = useAppContext();

  const [page, setPage] = useState(1);
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const observerRef = useRef(null);
  const lastElementRef = useRef(null);

  const placeholderText =
    type === "received"
      ? "New likes will appear here"
      : "Sent likes will appear here";

  const fetchLikes = useCallback(
    async (pageNumber) => {
      if (!type) return;
      if (!hasMore && pageNumber !== 1) return;

      setLoading(true);
      try {
        const response = await userActions.getReceivesData(type, pageNumber);
        if (response?.data) {
          setLikes((prev) => {
            const merged = [...prev, ...response.data];
            const unique = new Map();
            merged.forEach((item) => {
              unique.set(item.user_id, item);
            });
            return Array.from(unique.values());
          });

          if (response.data.length < 20) {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
      }
    },
    [type, hasMore]
  );

  useEffect(() => {
    if (!type) return;
    setPage(1);
    setLikes([]);
    setHasMore(true);
    fetchLikes(1);
  }, [type]);

  useEffect(() => {
    if (page === 1) return;
    fetchLikes(page);
  }, [page, fetchLikes]);

  useEffect(() => {
    if (!lastElementRef.current || !hasMore || loading) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setPage((prevPage) => prevPage + 1);
        observerRef.current.disconnect();
      }
    });

    observerRef.current.observe(lastElementRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [likes, hasMore, loading]);

  const getRoom = async (userId, name, token) => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    await chatWith({ name, recepientId: userId, token });
  };

  const chatWith = async ({ name, recepientId, token }) => {
    try {
      const response = await messageActon.createChatRoom({
        participantId: recepientId,
      });
      if (response?.conversationId) {
        const oldRoom = sessionStorage.getItem("conversationId");
        if (oldRoom) {
          socket?.emit("leave-room", oldRoom);
        }
        navigate(`/user/chat/with/${recepientId}?token=${encodeURIComponent(token || "")}`, {
          state: { conversationId: response.conversationId }
        });
      }
    } catch (error) {
      console.error("Chat error:", error);
    }
  };

  const handleProfileClick = (userId, name, token) => {
    if (!isPremium) {
      setShowUpgradeModal(true);
    } else {
      navigate(`/user/${resolveProfileId({ user_id: userId })}?token=${encodeURIComponent(token || "")}`, {
        state: {
          name: name,
          userId: resolveProfileId({ user_id: userId }),
          profile_token: token,
        },
      });
    }
  };

  const handleSelect = (userId) => {
    setSelected((prev) => (prev === userId ? null : userId));
  };
  return (
    <>
      <ul className={styles.nav}>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/likes/received");
          }}
          className={type === "received" ? styles.active : ""}
        >
          Received <div className={styles.indicator}></div>
        </li>

        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/likes/sent");
          }}
          className={type === "sent" ? styles.active : ""}
        >
          Sent <div className={styles.indicator}></div>
        </li>
      </ul>
      {Array.isArray(likes) && !loading && likes?.length === 0 && (
        <div className={styles.chatPlaceholder}>
          <img src={chatPlaceholder} alt="chat placeholder" />
          <p>{placeholderText}</p>
        </div>
      )}
      {isPremium !== true && (
        <div className={styles.upgradesections}>
          <p className={styles.heading} style={{}}>
            Upgrade and Unlock this feature
          </p>
          <p className={styles.discriptions} style={{}}>
            premium member can see who liked them sent unlimited massages & more
          </p>
          <button
            className="global-next-btn"
            style={{
              padding: "0px 0px 0px 0px",
              width: "150px",
              marginTop: "10px",
            }}
            onClick={() => navigate("/selectplan")}
          >
            Upgrade Now
          </button>
        </div>
      )}
      {likes?.length > 0 && (
        <div className={styles.profiles}>
          {Array.isArray(likes) &&
            likes.map((like, i) => {
              const isLastElement = i === likes.length - 1;
              return (
                <div
                  ref={(el) => {
                    if (isLastElement) lastElementRef.current = el;
                  }}
                  key={like.user_id}
                  className={`${
                    selected === like.user_id ? styles.selected : ""
                  } like`}
                  style={{
                    backgroundImage: `url(${getProfileImageUrl(like)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: isPremium ? "none" : "blur(3px)",
                    position: "relative",
                  }}
                  onClick={() => handleSelect(like.user_id)}
                >
                  <div>
                    {selected === like.user_id && (
                      <section
                        style={{
                          position: "absolute",
                          zIndex: 1,
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {type === "sent" && (
                          <button
                            className="message-icon"
                            style={{
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              getRoom(like.user_id, like.first_name, like.profile_token);
                            }}
                          >
                            <svg
                              width="46"
                              height="46"
                              viewBox="0 0 46 46"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="23" cy="23" r="23" fill="white" />
                              <path
                                d="M28.7609 12.5C30.3254 12.5 31.8304 13.1183 32.9375 14.2278C34.0459 15.335 34.6654 16.8283 34.6654 18.3917V27.6083C34.6654 30.8633 32.017 33.5 28.7609 33.5H17.2354C13.9792 33.5 11.332 30.8633 11.332 27.6083V18.3917C11.332 15.1367 13.9675 12.5 17.2354 12.5H28.7609ZM30.0804 18.5667C29.8354 18.5538 29.602 18.6367 29.4259 18.8L24.1654 23C23.4887 23.5612 22.5192 23.5612 21.832 23L16.582 18.8C16.2192 18.5317 15.7175 18.5667 15.4154 18.8817C15.1004 19.1967 15.0654 19.6983 15.3325 20.0483L15.4854 20.2L20.7937 24.3417C21.447 24.855 22.2392 25.135 23.0687 25.135C23.8959 25.135 24.702 24.855 25.3542 24.3417L30.617 20.13L30.7104 20.0367C30.9892 19.6983 30.9892 19.2083 30.6975 18.87C30.5354 18.6962 30.3125 18.59 30.0804 18.5667Z"
                                fill="#D7D7D7"
                              />
                            </svg>
                          </button>
                        )}

                        <button
                          className="profile-icon"
                          style={{
                            backgroundColor: "transparent",
                            border: "none",
                            borderRadius: "9999px",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProfileClick(
                              like.user_id,
                              like.first_name,
                              like.profile_token
                            );
                          }}
                        >
                          <svg
                            width="60"
                            height="60"
                            viewBox="0 0 60 60"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M30 7.5C17.625 7.5 7.5 17.625 7.5 30C7.5 42.375 17.625 52.5 30 52.5C42.375 52.5 52.5 42.375 52.5 30C52.5 17.625 42.375 7.5 30 7.5ZM30 16.25C32.75 16.25 35 18.5 35 21.25C35 24 32.75 26.25 30 26.25C27.25 26.25 25 24 25 21.25C25 18.5 27.25 16.25 30 16.25ZM21.5 42.5C19.2909 42.5 17.4376 40.6633 18.1374 38.5679C19.795 33.6038 24.4971 30 30 30C35.5029 30 40.205 33.6038 41.8626 38.5679C42.5624 40.6633 40.7091 42.5 38.5 42.5H21.5Z"
                              fill="white"
                            />
                            <path
                              d="M21.5 42.5C19.2909 42.5 17.4376 40.6633 18.1374 38.5679C19.795 33.6038 24.4971 30 30 30C35.5029 30 40.205 33.6038 41.8626 38.5679C42.5624 40.6633 40.7091 42.5 38.5 42.5H21.5Z"
                              fill="url(#paint0_linear_969_14272)"
                            />
                            <path
                              d="M30 16.25C32.75 16.25 35 18.5 35 21.25C35 24 32.75 26.25 30 26.25C27.25 26.25 25 24 25 21.25C25 18.5 27.25 16.25 30 16.25Z"
                              fill="url(#paint1_linear_969_14272)"
                            />
                            <defs>
                              <linearGradient
                                id="paint0_linear_969_14272"
                                x1="7.5"
                                y1="30"
                                x2="52.5"
                                y2="30"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#FC8C66" />
                                <stop offset="1" stopColor="#F76A7B" />
                              </linearGradient>
                              <linearGradient
                                id="paint1_linear_969_14272"
                                x1="7.5"
                                y1="30"
                                x2="52.5"
                                y2="30"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#FC8C66" />
                                <stop offset="1" stopColor="#F76A7B" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </button>
                      </section>
                    )}
                    <p>{like.first_name}</p>
                    <p>
                      {like.job}, {like.country}
                    </p>
                  </div>
                </div>
              );
            })}

          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                animation="pulse"
                sx={{ borderRadius: "12px" }}
                variant="rounded"
                width={160}
                height={100}
              />
            ))}
        </div>
      )}
      <UpgradeModal show={showUpgradeModal} setShow={setShowUpgradeModal} />
    </>
  );
}
